import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { TELAS, type Rota } from '@/shared/lib/cotas';
import { obterSessaoOperador } from '@/shared/lib/sessao-operador';
import { obterSessao, podeFazer } from '@/shared/lib/sessao';
import { Botao } from '@/shared/ui/Botao';
import { AppShell } from './AppShell';
import { OperacaoShell } from './OperacaoShell';

/* eslint-disable @typescript-eslint/only-throw-error --
 * `throw redirect(...)` é a forma como o TanStack Router interrompe uma
 * navegação: o objeto devolvido por `redirect` não é um `Error`, e é o próprio
 * roteador que o intercepta. Não há alternativa dentro de `beforeLoad`.
 */

/**
 * Rotas.
 *
 * Definidas em código, não por convenção de arquivo: o projeto não usa o plugin
 * de rotas do Vite, e o ganho de gerar a árvore não compensaria uma etapa a
 * mais de build para dois módulos.
 *
 * Cada página entra por `lazyRouteComponent`, então o usuário de Cadastros não
 * baixa o código de Usuários (.spec/004, seção 3.3).
 *
 * **Toda rota do plano do cliente tem guarda de permissão** (`guardaDe`), e a
 * permissão vem do catálogo de cotas. Antes só a raiz consultava `podeFazer`, e
 * digitar o caminho de um módulo alheio montava a tela inteira — com o aviso de
 * acesso negado e, logo abaixo, o estado vazio dizendo que o cadastro está
 * vazio. Com a guarda, o chunk da página nem chega a ser baixado.
 */

const rotaRaiz = createRootRoute({ component: Outlet });

const rotaLogin = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/login',
  // Quem já está autenticado não vê a tela de entrada: voltar para ela pelo
  // histórico daria a impressão de que a sessão caiu.
  // Vai para a raiz, e não direto para `/empresas`: quem tem só o papel de
  // Compras não enxerga empresas, e cairia numa tela que a própria navegação
  // esconde dele. É `rotaInicio` que sabe qual é o primeiro módulo de cada um.
  beforeLoad: () => {
    if (obterSessao()) throw redirect({ to: '/' });
  },
  component: lazyRouteComponent(() => import('@/features/auth/pages/LoginPage'), 'LoginPage'),
});

/**
 * As duas telas de credencial sem sessão.
 *
 * Fora de `rotaApp`, como o login: quem chega aqui não tem sessão — é o link do
 * e-mail, ou alguém que esqueceu a senha. E fora de `rotaLogin`, porque quem já
 * está autenticado **pode** abrir o link de convite de outra pessoa no mesmo
 * navegador; redirecionar para a raiz o mandaria para a conta errada.
 */
const rotaEsqueciSenha = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/esqueci-senha',
  component: lazyRouteComponent(
    () => import('@/features/auth/pages/EsqueciSenhaPage'),
    'EsqueciSenhaPage',
  ),
});

const rotaDefinirSenha = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/definir-senha',
  // O token fica na busca da URL: é o link colado do e-mail, e tem de
  // sobreviver a um navegador que nunca abriu o sistema.
  validateSearch: (busca: Record<string, unknown>): { token?: string } =>
    typeof busca.token === 'string' ? { token: busca.token } : {},
  component: lazyRouteComponent(
    () => import('@/features/auth/pages/DefinirSenhaPage'),
    'DefinirSenhaPage',
  ),
});

/**
 * Camada autenticada.
 *
 * A guarda lê a sessão de forma **síncrona** do store, e por isso decide antes
 * de qualquer componente montar — a rota protegida não chega a piscar.
 * Isto é conveniência de navegação: quem autoriza de fato é a API.
 */
/**
 * A página do fornecedor, aberta pelo link do e-mail da cotação (decisão 117).
 *
 * Fora de `rotaApp`, como a definição de senha: quem abre não tem sessão, e o
 * `beforeLoad` de lá o mandaria ao login. O token vai no caminho, e não na
 * busca: é o link inteiro que o fornecedor recebe, e é por ele que a API acha a
 * cotação.
 */
const rotaRespostaDaCotacao = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/cotacao/$token',
  component: lazyRouteComponent(
    () => import('@/features/cotacao-publica/pages/RespostaDaCotacaoPage'),
    'RespostaDaCotacaoPage',
  ),
});

const rotaApp = createRoute({
  getParentRoute: () => rotaRaiz,
  id: 'autenticado',
  beforeLoad: () => {
    if (!obterSessao()) throw redirect({ to: '/login' });
  },
  component: AppShell,
});

/**
 * Primeiro destino de cada perfil, **em ordem de prioridade**.
 *
 * Tabela, e não uma fila de `if`: a lista cresce a cada módulo, e a versão em
 * `if` já passava do limite de ramificações do lint — o que empurra para
 * "aumenta o teto" em vez de "não é ramificação, é dado".
 *
 * A ordem é a única coisa que importa aqui, e ela tem uma decisão dentro:
 * contratos vem antes de clientes porque o papel Contratos carrega
 * `cadastros.cliente.*` junto, e cair no cadastro de clientes deixaria o usuário
 * na tela auxiliar do próprio módulo em vez de na principal.
 */
const PRIMEIRO_MODULO = [
  ['cadastros.empresa.ver', '/empresas'],
  ['cadastros.fornecedor.ver', '/fornecedores'],
  /*
    A nota de serviço **antes do contrato** e de clientes (`.spec/015`, decisão
    100). O papel Faturamento carrega `contratos.contrato.ver` e
    `cadastros.cliente.*` junto — lê o contrato que fatura e mantém o tomador —,
    e com esta linha depois das duas ele cairia numa tela de consulta, ou no
    cadastro auxiliar, em vez de na tela do dia.

    Até a decisão 100 ela vinha depois do contrato: a permissão era
    `contratos.nota_servico.ver`, e quem a tinha era o papel Contratos, que já
    tinha parado numa linha anterior. Hoje Contratos não a tem, e nada muda
    para ele.
  */
  ['faturamento.nota_servico.ver', '/notas-servico'],
  ['contratos.contrato.ver', '/contratos'],
  ['cadastros.cliente.ver', '/clientes'],
  // A solicitação antes dos cadastros de compras: é a tela que o comprador abre
  // todo dia, e o grupo e o insumo se mexem uma vez por trimestre.
  /*
    A fila do autorizador **antes** da solicitação (decisão 109): o Autorizador
    de compras tem `compras.solicitacao.ver` para abrir o que vai decidir, e com
    esta linha depois ele cairia na listagem de solicitações em vez da mesa
    dele. Compras não tem `compras.aprovacao.fila`, e para ele nada muda.
  */
  ['compras.aprovacao.fila', '/aprovacoes-compra'],
  ['compras.solicitacao.ver', '/solicitacoes-compra'],
  // A cotação logo depois, e antes dos cadastros: é a segunda tela que o
  // comprador abre no dia, na ordem em que a compra acontece.
  ['compras.cotacao.ver', '/cotacoes-preco'],
  // E o pedido logo depois, fechando a sequência da compra. Continua antes dos
  // cadastros pelo mesmo motivo: é tela de todo dia.
  ['compras.pedido.ver', '/pedidos-compra'],
  // A lista de compra direta depois dos três: ela é o caminho curto, e quem tem
  // os dois caminhos abre o dia pelo longo — a fila de solicitações. A linha
  // existe para o papel que só recebe `compras.lista_compra_direta.*`, o
  // canteiro do dia em que os dois acessos forem separados, e sem ela ele cairia
  // numa tela que não alcança.
  ['compras.lista_compra_direta.ver', '/listas-compra-direta'],
  // A nota logo depois do pedido, fechando a sequência da compra e antes dos
  // cadastros: para a controladoria é a tela de todo dia — a fila de conferência
  // do que chegou.
  ['controladoria.nota.ver', '/notas-compra'],
  ['cadastros.grupo_insumo.ver', '/grupos-insumos'],
  ['cadastros.insumo.ver', '/insumos'],
  ['controladoria.estoque.ver', '/lancamentos-estoque'],
  // O consumo depois do lançamento manual, e antes do cadastro de depósito: para
  // quem só tem `consumo.*` — o almoxarife de campo do dia em que os dois acessos
  // forem separados — é a única tela do dia.
  ['consumo.lancamento.ver', '/lancamentos-consumo'],
  // A devolução logo depois: é a outra ponta do mesmo documento, e quem só a tem
  // é o almoxarife que recebe a sobra do canteiro.
  ['consumo.devolucao.ver', '/devolucoes-consumo'],
  // A transferência depois das duas: ela é o movimento mais raro dos três, e
  // quem tem só ela não é o perfil de ninguém — a linha existe para o papel que
  // perdeu `controladoria.estoque.*` mas continua movendo material entre obras.
  ['controladoria.transferencia.ver', '/transferencias-estoque'],
  ['cadastros.deposito.ver', '/depositos'],
  // O contas a pagar antes do caixa: para quem tem o papel Pagamento, é a única
  // tela do dia — e ele não recebe `financeiro.*`, então cairia no plano de
  // contas se esta linha viesse depois.
  ['pagamento.conta_pagar.ver', '/contas-pagar'],
  // O contas a receber logo depois, e pelo mesmo motivo: quem tem o papel
  // Recebimento não recebe `financeiro.*` nem `pagamento.*`, e sem esta linha
  // cairia numa tela que não alcança.
  ['recebimento.conta_receber.ver', '/contas-receber'],
  ['financeiro.lancamento_conta_corrente.ver', '/lancamentos-conta-corrente'],
  ['cadastros.conta_contabil.ver', '/contas-contabeis'],
  ['admin.usuario.ver', '/usuarios'],
  ['admin.conta.ver', '/conta'],
] as const;

/**
 * A tela de cada caminho, para a guarda de permissão.
 *
 * O catálogo de cotas já sabe qual permissão abre cada tela — é o mesmo dado
 * que decide o item de menu e o resultado da busca. Repetir a lista aqui criaria
 * a chance de uma rota nova entrar no roteador com a permissão de outra, ou com
 * nenhuma, e o sintoma seria exatamente o que esta guarda existe para impedir.
 */
const TELA_POR_CAMINHO = new Map(Object.values(TELAS).map((tela) => [tela.para, tela] as const));

/**
 * A guarda de permissão de uma rota.
 *
 * **Por que ela existe.** Sem guarda, digitar `/empresas` sem a permissão
 * montava a tela inteira: cabeçalho, filtros, aviso de acesso negado e, logo
 * abaixo, o estado vazio — "Nenhuma empresa cadastrada". A API recusava
 * corretamente, mas quem lia a tela via um cadastro vazio, que é a resposta
 * errada dada com a mesma cara da certa. Autorização continua sendo do servidor;
 * o que muda aqui é a tela parar de dizer uma coisa que não é verdade.
 *
 * A permissão é a de **ver**, inclusive em `/novo` e `/$id`: quem não enxerga a
 * listagem não tem o que fazer no formulário, e o que a tela permite depois —
 * criar, editar — ela mesma decide com `usePode`, mostrando o aviso de somente
 * leitura.
 *
 * Falhar na leitura do catálogo é erro de programação, não de uso: a rota sem
 * tela registrada ficaria sem guarda nenhuma, em silêncio. Melhor quebrar na
 * carga do módulo, onde quem acabou de escrever a rota está olhando.
 */
function guardaDe(caminho: Rota): () => void {
  const tela = TELA_POR_CAMINHO.get(caminho);
  if (!tela) throw new Error(`Rota sem tela registrada em cotas.ts: ${caminho}`);

  return () => {
    if (podeFazer(tela.permissao)) return;

    throw redirect({ to: '/sem-acesso', search: { tela: tela.rotulo } });
  };
}

/** Entrada do sistema: manda para o primeiro módulo que o usuário alcança. */
const rotaInicio = createRoute({
  getParentRoute: () => rotaApp,
  path: '/',
  beforeLoad: () => {
    const destino = PRIMEIRO_MODULO.find(([permissao]) => podeFazer(permissao));
    if (destino) throw redirect({ to: destino[1] });
  },
  component: SemModulo,
});

const rotaEmpresas = createRoute({
  getParentRoute: () => rotaApp,
  path: '/empresas',
  beforeLoad: guardaDe('/empresas'),
  component: lazyRouteComponent(
    () => import('@/features/empresas/pages/EmpresasListPage'),
    'EmpresasListPage',
  ),
});

const rotaEmpresaNova = createRoute({
  getParentRoute: () => rotaApp,
  path: '/empresas/novo',
  beforeLoad: guardaDe('/empresas'),
  component: lazyRouteComponent(
    () => import('@/features/empresas/pages/EmpresaFormPage'),
    'EmpresaFormPage',
  ),
});

const rotaEmpresaEdicao = createRoute({
  getParentRoute: () => rotaApp,
  path: '/empresas/$id',
  beforeLoad: guardaDe('/empresas'),
  component: lazyRouteComponent(
    () => import('@/features/empresas/pages/EmpresaFormPage'),
    'EmpresaFormPage',
  ),
});

const rotaFornecedores = createRoute({
  getParentRoute: () => rotaApp,
  path: '/fornecedores',
  beforeLoad: guardaDe('/fornecedores'),
  component: lazyRouteComponent(
    () => import('@/features/fornecedores/pages/FornecedoresListPage'),
    'FornecedoresListPage',
  ),
});

const rotaFornecedorNovo = createRoute({
  getParentRoute: () => rotaApp,
  path: '/fornecedores/novo',
  beforeLoad: guardaDe('/fornecedores'),
  component: lazyRouteComponent(
    () => import('@/features/fornecedores/pages/FornecedorFormPage'),
    'FornecedorFormPage',
  ),
});

const rotaFornecedorEdicao = createRoute({
  getParentRoute: () => rotaApp,
  path: '/fornecedores/$id',
  beforeLoad: guardaDe('/fornecedores'),
  component: lazyRouteComponent(
    () => import('@/features/fornecedores/pages/FornecedorFormPage'),
    'FornecedorFormPage',
  ),
});

const rotaClientes = createRoute({
  getParentRoute: () => rotaApp,
  path: '/clientes',
  beforeLoad: guardaDe('/clientes'),
  component: lazyRouteComponent(
    () => import('@/features/clientes/pages/ClientesListPage'),
    'ClientesListPage',
  ),
});

const rotaClienteNovo = createRoute({
  getParentRoute: () => rotaApp,
  path: '/clientes/novo',
  beforeLoad: guardaDe('/clientes'),
  component: lazyRouteComponent(
    () => import('@/features/clientes/pages/ClienteFormPage'),
    'ClienteFormPage',
  ),
});

const rotaClienteEdicao = createRoute({
  getParentRoute: () => rotaApp,
  path: '/clientes/$id',
  beforeLoad: guardaDe('/clientes'),
  component: lazyRouteComponent(
    () => import('@/features/clientes/pages/ClienteFormPage'),
    'ClienteFormPage',
  ),
});

/**
 * Cadastros financeiros.
 *
 * Quatro cadastros com a mesma forma — listagem, `/novo` e `/$id`. A fábrica
 * evita doze blocos idênticos de seis linhas, em que a única variação é o
 * caminho e o componente. O `path` continua literal em cada chamada, que é o
 * que o roteador precisa para tipar os `Link`.
 */
function rotasDeCadastro<T extends Rota>(
  caminho: T,
  lista: () => Promise<Record<string, unknown>>,
  nomeLista: string,
  formulario: () => Promise<Record<string, unknown>>,
  nomeFormulario: string,
) {
  // A mesma guarda nas três: a permissão de ver é o que abre o módulo, e o
  // formulário de um cadastro que o usuário não enxerga não tem o que mostrar.
  const guarda = guardaDe(caminho);

  return [
    createRoute({
      getParentRoute: () => rotaApp,
      path: caminho,
      beforeLoad: guarda,
      component: lazyRouteComponent(lista, nomeLista),
    }),
    createRoute({
      getParentRoute: () => rotaApp,
      path: `${caminho}/novo` as const,
      beforeLoad: guarda,
      component: lazyRouteComponent(formulario, nomeFormulario),
    }),
    createRoute({
      getParentRoute: () => rotaApp,
      path: `${caminho}/$id` as const,
      beforeLoad: guarda,
      component: lazyRouteComponent(formulario, nomeFormulario),
    }),
  ] as const;
}

const rotasContaContabil = rotasDeCadastro(
  '/contas-contabeis',
  () => import('@/features/financeiro/pages/ContasContabeisListPage'),
  'ContasContabeisListPage',
  () => import('@/features/financeiro/pages/ContaContabilFormPage'),
  'ContaContabilFormPage',
);

const rotasHistoricoBancario = rotasDeCadastro(
  '/historicos-bancarios',
  () => import('@/features/financeiro/pages/HistoricosBancariosListPage'),
  'HistoricosBancariosListPage',
  () => import('@/features/financeiro/pages/HistoricoBancarioFormPage'),
  'HistoricoBancarioFormPage',
);

const rotasHistoricoFinanceiro = rotasDeCadastro(
  '/historicos-financeiros',
  () => import('@/features/financeiro/pages/HistoricosFinanceirosListPage'),
  'HistoricosFinanceirosListPage',
  () => import('@/features/financeiro/pages/HistoricoFinanceiroFormPage'),
  'HistoricoFinanceiroFormPage',
);

const rotasContaCorrente = rotasDeCadastro(
  '/contas-correntes',
  () => import('@/features/financeiro/pages/ContasCorrentesListPage'),
  'ContasCorrentesListPage',
  () => import('@/features/financeiro/pages/ContaCorrenteFormPage'),
  'ContaCorrenteFormPage',
);

/**
 * Lançamentos de estoque.
 *
 * Mesma forma dos cadastros — listagem, `/novo` e `/$id` —, ainda que o `/$id`
 * abra em **consulta**, não em edição: lançamento gravado não se altera, porque
 * o custo médio que ele formou já pode ter sido consumido por movimentos
 * seguintes. O que a fábrica define é a navegação, não o que a tela permite.
 */
const rotasLancamentoEstoque = rotasDeCadastro(
  '/lancamentos-estoque',
  () => import('@/features/lancamentos-estoque/pages/LancamentosEstoqueListPage'),
  'LancamentosEstoqueListPage',
  () => import('@/features/lancamentos-estoque/pages/LancamentoEstoqueFormPage'),
  'LancamentoEstoqueFormPage',
);

/**
 * Lançamentos de consumo.
 *
 * Mesma forma do lançamento de estoque, e o `/$id` abre em **consulta** pelo
 * mesmo motivo: o consumo já baixou o saldo pelo custo médio do momento, e
 * alterá-lo exigiria desfazer esse efeito. A tela vai além e esconde também o
 * botão de excluir quando o consumo veio de uma nota confirmada — quem o desfaz
 * ali é a exclusão da nota. O que a fábrica define é a navegação, não o que a
 * tela permite.
 */
const rotasLancamentoConsumo = rotasDeCadastro(
  '/lancamentos-consumo',
  () => import('@/features/lancamentos-consumo/pages/LancamentosConsumoListPage'),
  'LancamentosConsumoListPage',
  () => import('@/features/lancamentos-consumo/pages/LancamentoConsumoFormPage'),
  'LancamentoConsumoFormPage',
);

/**
 * Devolução de consumo (`.spec/015`, decisão 82). Sem edição, como o consumo: a
 * rota `$id` abre a consulta, e corrigir é excluir e relançar.
 */
const rotasDevolucaoConsumo = rotasDeCadastro(
  '/devolucoes-consumo',
  () => import('@/features/devolucoes-consumo/pages/DevolucoesConsumoListPage'),
  'DevolucoesConsumoListPage',
  () => import('@/features/devolucoes-consumo/pages/DevolucaoConsumoFormPage'),
  'DevolucaoConsumoFormPage',
);

/**
 * Transferências de estoque entre depósitos.
 *
 * Mesma forma do lançamento de estoque e do consumo, e o `/$id` abre em
 * **consulta** pelo mesmo motivo — a transferência já mexeu no saldo dos dois
 * depósitos, e alterá-la exigiria desfazer o efeito nas duas pontas. O que a
 * fábrica define é a navegação, não o que a tela permite.
 */
const rotasTransferenciaEstoque = rotasDeCadastro(
  '/transferencias-estoque',
  () => import('@/features/transferencias-estoque/pages/TransferenciasEstoqueListPage'),
  'TransferenciasEstoqueListPage',
  () => import('@/features/transferencias-estoque/pages/TransferenciaEstoqueFormPage'),
  'TransferenciaEstoqueFormPage',
);

/**
 * Solicitações de compra.
 *
 * Mesma forma dos cadastros — listagem, `/novo` e `/$id` —, e aqui o `/$id` abre
 * mesmo em **edição**: a solicitação não movimenta saldo, então corrigir não
 * exige desfazer efeito nenhum. Quando o documento já foi encerrado, ou quando
 * um pedido o cobriu em parte, a própria tela cai em consulta — o que a fábrica
 * define é a navegação, não o que a tela permite.
 */
const rotasSolicitacaoCompra = rotasDeCadastro(
  '/solicitacoes-compra',
  () => import('@/features/solicitacoes-compra/pages/SolicitacoesCompraListPage'),
  'SolicitacoesCompraListPage',
  () => import('@/features/solicitacoes-compra/pages/SolicitacaoCompraFormPage'),
  'SolicitacaoCompraFormPage',
);

/**
 * Cotações de preço.
 *
 * Mesma forma dos cadastros, e o `/$id` abre em **edição**: a cotação não
 * movimenta saldo nenhum, e acrescentar a proposta que chegou hoje a um
 * documento aberto na semana passada é o uso normal do módulo. Quando uma
 * proposta já virou pedido, a própria tela cai em consulta — o que a fábrica
 * define é a navegação, não o que a tela permite.
 */
const rotasCotacaoPreco = rotasDeCadastro(
  '/cotacoes-preco',
  () => import('@/features/cotacoes-preco/pages/CotacoesPrecoListPage'),
  'CotacoesPrecoListPage',
  () => import('@/features/cotacoes-preco/pages/CotacaoPrecoFormPage'),
  'CotacaoPrecoFormPage',
);

/**
 * Pedidos de compra.
 *
 * Mesma forma dos cadastros, e o `/$id` abre em **edição**: o pedido não
 * movimenta estoque, então corrigir não exige desfazer efeito nenhum. Quando uma
 * nota de compra já registrou entrega, ou quando o pedido foi encerrado, a
 * própria tela cai em consulta — o que a fábrica define é a navegação, não o que
 * a tela permite.
 */
const rotasPedidoCompra = rotasDeCadastro(
  '/pedidos-compra',
  () => import('@/features/pedidos-compra/pages/PedidosCompraListPage'),
  'PedidosCompraListPage',
  () => import('@/features/pedidos-compra/pages/PedidoCompraFormPage'),
  'PedidoCompraFormPage',
);

/**
 * Notas de compra.
 *
 * Mesma forma dos cadastros, e o `/$id` abre em **edição**: enquanto a nota está
 * pendente ela não tem efeito nenhum — nada de estoque, nada de financeiro —, e
 * corrigir a conferência é o uso normal do módulo. Depois de confirmada a própria
 * tela cai em consulta, porque alterá-la exigiria desfazer estoque e títulos. O
 * que a fábrica define é a navegação, não o que a tela permite.
 */
const rotasNotaCompra = rotasDeCadastro(
  '/notas-compra',
  () => import('@/features/notas-compra/pages/NotasCompraListPage'),
  'NotasCompraListPage',
  () => import('@/features/notas-compra/pages/NotaCompraFormPage'),
  'NotaCompraFormPage',
);

/**
 * Entrada de nota por pedido de compra.
 *
 * Rota própria, e não um botão dentro do formulário de nota: o que se lê aqui são
 * **pedidos**, com filtro obrigatório de empresa e fornecedor e seleção múltipla,
 * e espremer isso num diálogo daria uma janela com uma tabela paginada dentro. É
 * a mesma forma da geração de pedidos a partir das cotações, pelo mesmo motivo.
 *
 * Fica **antes** de `rotasNotaCompra` no arquivo por clareza de leitura; a árvore
 * do roteador casa por caminho, e `/notas-compra/por-pedido` é um caminho
 * distinto de `/notas-compra/$id`.
 */
/**
 * Entrada de nota pelo XML da NF-e (`.spec/015`, decisão 19).
 *
 * Rota irmã da entrada por pedido, e não uma aba dentro dela: as duas partem de
 * coisas diferentes — lá o usuário escolhe pedidos, aqui escolhe um arquivo —, e
 * as duas terminam no mesmo formulário de nota. Cota própria (`CTL·05`) pelo
 * mesmo motivo: o suporte precisa poder dizer "abra a CTL·05".
 *
 * **O arquivo é lido no navegador e não sobe para o servidor.** Não há rota de
 * importação, e por isso não há `beforeLoad` diferente do da tela: o que a
 * permissão protege é a criação da nota, que acontece depois, no formulário.
 */
const rotaEntradaPorXml = createRoute({
  getParentRoute: () => rotaApp,
  path: '/notas-compra/por-xml',
  beforeLoad: guardaDe('/notas-compra/por-xml'),
  component: lazyRouteComponent(
    () => import('@/features/notas-compra/pages/EntradaPorXmlPage'),
    'EntradaPorXmlPage',
  ),
});

const rotaEntradaPorPedido = createRoute({
  getParentRoute: () => rotaApp,
  path: '/notas-compra/por-pedido',
  // A guarda é a da **CTL·03**, que pede `controladoria.nota.criar` — e não a da
  // listagem de notas: esta tela lança, não consulta.
  beforeLoad: guardaDe('/notas-compra/por-pedido'),
  component: lazyRouteComponent(
    () => import('@/features/notas-compra/pages/EntradaPorPedidoPage'),
    'EntradaPorPedidoPage',
  ),
});

/**
 * Cardex de estoque.
 *
 * Rota única, sem `/novo` nem `/$id`: é consulta do livro-razão, e o razão não
 * se cria nem se abre registro a registro. É a mesma forma do extrato de conta
 * corrente, pelo mesmo motivo.
 */
/**
 * Geração de pedidos a partir das cotações.
 *
 * Rota própria, e não um botão dentro da listagem de pedidos: o que se lê aqui
 * são **propostas de cotação**, com filtros próprios e seleção múltipla, e
 * espremer isso num diálogo da outra tela daria uma janela com seis filtros e
 * uma tabela paginada dentro.
 */
/**
 * Distribuição de consumo (`CSM·02`).
 *
 * Página única, como a geração de pedidos: ela não abre registro nenhum — grava
 * lançamentos de consumo, e quem os consulta depois é a `CSM·01`. Pede
 * `consumo.lancamento.criar`, porque é o que a tela faz.
 */
const rotaDistribuicaoConsumo = createRoute({
  getParentRoute: () => rotaApp,
  path: '/distribuicao-consumo',
  beforeLoad: guardaDe('/distribuicao-consumo'),
  component: lazyRouteComponent(
    () => import('@/features/distribuicao-consumo/pages/DistribuicaoConsumoPage'),
    'DistribuicaoConsumoPage',
  ),
});

const rotaGeracaoDePedidos = createRoute({
  getParentRoute: () => rotaApp,
  path: '/gerar-pedidos',
  // A CMP·04 pede `compras.pedido.criar`, pelo mesmo motivo da entrada por
  // pedido: quem só consulta pedidos não gera nenhum.
  beforeLoad: guardaDe('/gerar-pedidos'),
  component: lazyRouteComponent(
    () => import('@/features/pedidos-compra/pages/GeracaoDePedidosPage'),
    'GeracaoDePedidosPage',
  ),
});

/**
 * Listas de compra direta.
 *
 * Mesma forma dos cadastros — listagem, `/novo` e `/$id` —, e o `/$id` abre em
 * **edição**: a lista não movimenta estoque, não gera financeiro e não escreve em
 * documento nenhum, então corrigir não exige desfazer efeito algum. Depois de
 * virar pedido, a própria tela cai em consulta — o que a fábrica define é a
 * navegação, não o que a tela permite.
 */
const rotasListaCompraDireta = rotasDeCadastro(
  '/listas-compra-direta',
  () => import('@/features/listas-compra-direta/pages/ListasCompraDiretaListPage'),
  'ListasCompraDiretaListPage',
  () => import('@/features/listas-compra-direta/pages/ListaCompraDiretaFormPage'),
  'ListaCompraDiretaFormPage',
);

/**
 * Geração de pedidos a partir das listas de compra direta.
 *
 * Rota própria, ao lado da geração por cotação e pelo mesmo motivo: o que se lê
 * aqui são documentos de outro recurso, com filtros próprios e seleção múltipla,
 * e espremer isso num diálogo da listagem daria uma janela com três filtros e uma
 * tabela paginada dentro.
 *
 * **Separada da CMP·04**, e não uma aba dela: as duas partem de coisas
 * diferentes — lá o comprador escolhe propostas que ele mesmo marcou como
 * vencedoras, aqui ele escolhe listas inteiras cujo fornecedor já estava
 * acordado — e o suporte precisa poder dizer "abra a CMP·07" sem explicar em qual
 * aba clicar.
 */
const rotaGeracaoPorLista = createRoute({
  getParentRoute: () => rotaApp,
  path: '/gerar-pedidos-de-listas',
  // A CMP·07 pede `compras.pedido.criar`, pelo mesmo motivo da CMP·04: quem só
  // monta listas não gera pedido nenhum.
  beforeLoad: guardaDe('/gerar-pedidos-de-listas'),
  component: lazyRouteComponent(
    () => import('@/features/listas-compra-direta/pages/GeracaoPorListaPage'),
    'GeracaoPorListaPage',
  ),
});

/**
 * Painel de compras.
 *
 * Rota única, sem `/novo` nem `/$id`: é leitura agregada dos três documentos do
 * módulo, e não há registro de painel a abrir. É a mesma forma do cardex e do
 * extrato de conta corrente, pelo mesmo motivo.
 */
/**
 * Aprovações pendentes (`CMP·08`, decisão 109) — a mesa do autorizador de
 * compras. Rota única: a fila dos três documentos, e cada um se abre na tela
 * dele.
 */
const rotaAprovacoesPendentes = createRoute({
  getParentRoute: () => rotaApp,
  path: '/aprovacoes-compra',
  beforeLoad: guardaDe('/aprovacoes-compra'),
  component: lazyRouteComponent(
    () => import('@/features/aprovacao-compra/pages/AprovacoesPendentesPage'),
    'AprovacoesPendentesPage',
  ),
});

/**
 * Enviar para aprovação (`CMP·09`, decisão 110) — a mesa de quem lança, irmã da
 * `CMP·08`.
 */
const rotaEnvioParaAprovacao = createRoute({
  getParentRoute: () => rotaApp,
  path: '/envio-aprovacao-compra',
  beforeLoad: guardaDe('/envio-aprovacao-compra'),
  component: lazyRouteComponent(
    () => import('@/features/aprovacao-compra/pages/EnvioParaAprovacaoPage'),
    'EnvioParaAprovacaoPage',
  ),
});

/** Enviar pedidos por e-mail em lote (`CMP·10`, decisão 116). */
const rotaEnvioDePedidos = createRoute({
  getParentRoute: () => rotaApp,
  path: '/envio-pedidos-compra',
  beforeLoad: guardaDe('/envio-pedidos-compra'),
  component: lazyRouteComponent(
    () => import('@/features/pedidos-compra/pages/EnvioDePedidosPage'),
    'EnvioDePedidosPage',
  ),
});

const rotaPainelDeCompras = createRoute({
  getParentRoute: () => rotaApp,
  path: '/painel-compras',
  beforeLoad: guardaDe('/painel-compras'),
  component: lazyRouteComponent(
    () => import('@/features/painel-compras/pages/PainelDeComprasPage'),
    'PainelDeComprasPage',
  ),
});

const rotaCardex = createRoute({
  getParentRoute: () => rotaApp,
  path: '/cardex',
  beforeLoad: guardaDe('/cardex'),
  component: lazyRouteComponent(() => import('@/features/cardex/pages/CardexPage'), 'CardexPage'),
});

/**
 * Estoque de um depósito.
 *
 * Rota única, como o cardex, e pelo mesmo motivo: é consulta de saldo, e saldo
 * não se cria nem se abre registro a registro. A diferença entre as duas é o
 * eixo — o cardex fixa um material e abre o tempo; esta fixa um depósito e abre
 * o catálogo.
 */
const rotaEstoqueDoDeposito = createRoute({
  getParentRoute: () => rotaApp,
  path: '/estoque-deposito',
  beforeLoad: guardaDe('/estoque-deposito'),
  component: lazyRouteComponent(
    () => import('@/features/estoque-deposito/pages/EstoqueDoDepositoPage'),
    'EstoqueDoDepositoPage',
  ),
});

/**
 * Tipos de movimentação de estoque.
 *
 * Cadastro de apoio do estoque, com a mesma forma dos demais. Fica ao lado do
 * depósito no menu porque os dois são escolhidos no mesmo lançamento: um diz
 * onde o material está, o outro diz por que ele se moveu.
 */
const rotasTipoMovimentacaoEstoque = rotasDeCadastro(
  '/tipos-movimentacao-estoque',
  () => import('@/features/tipos-movimentacao-estoque/pages/TiposMovimentacaoEstoqueListPage'),
  'TiposMovimentacaoEstoqueListPage',
  () => import('@/features/tipos-movimentacao-estoque/pages/TipoMovimentacaoEstoqueFormPage'),
  'TipoMovimentacaoEstoqueFormPage',
);

/**
 * Itens de orçamento.
 *
 * Catálogo do comercial, com a mesma forma dos demais cadastros. Fica em
 * Contratos no menu, ao lado do contrato e da obra: na lógica do processo ele
 * vem **antes** dos dois — orça-se, depois se contrata, depois se abre o
 * canteiro — e quem mantém a lista é quem orça.
 *
 * A guarda sai de `TELAS['CAD·16']`, como em todo `rotasDeCadastro`: a permissão
 * é `contratos.item_orcamento.ver`, e não a de insumo, embora o seletor do insumo
 * leia este catálogo. Ler a lista dentro de outra tela e abrir a tela do
 * catálogo são dois acessos diferentes.
 */
const rotasItemOrcamento = rotasDeCadastro(
  '/itens-orcamento',
  () => import('@/features/itens-orcamento/pages/ItensOrcamentoListPage'),
  'ItensOrcamentoListPage',
  () => import('@/features/itens-orcamento/pages/ItemOrcamentoFormPage'),
  'ItemOrcamentoFormPage',
);

/**
 * Orçamentos.
 *
 * Documento com mestre e itens, e mesmo assim `rotasDeCadastro`: o que aquele
 * ajudante monta é o trio listagem / `novo` / `$id` com uma guarda só, e é
 * exatamente a forma desta tela. O contrato e a obra usam o mesmo ajudante pela
 * mesma razão — ele não é sobre cadastro, é sobre a forma das rotas.
 *
 * A guarda sai de `TELAS['CTR·04']`: `contratos.orcamento.ver`, pelo curinga
 * `contratos.*` do papel Contratos.
 */
const guardaOrcamento = guardaDe('/orcamentos');

const formularioDeOrcamento = lazyRouteComponent(
  () => import('@/features/orcamentos/pages/OrcamentoFormPage'),
  'OrcamentoFormPage',
);

/**
 * As três escritas à mão, e não por `rotasDeCadastro`, por **um** motivo:
 * `?copiarDe=` no `/novo` (`.spec/015`, decisão 59).
 *
 * Duplicar abre o formulário de orçamento novo pré-preenchido com o original, e
 * o id do original viaja na busca da URL — que é o lugar certo para ele: o
 * estado de navegação some no recarregar da página, e a pessoa que atualiza a
 * aba veria um formulário em branco sem entender o que aconteceu.
 *
 * `validateSearch` é o que torna o parâmetro **tipado** nas duas pontas: o
 * `navigate` do botão Duplicar não compila com a chave errada, e a página o lê
 * sem asserção. O ajudante genérico não tem como oferecer isso sem ganhar uma
 * prop de busca que nenhuma outra tela usaria.
 */
const rotasOrcamento = [
  createRoute({
    getParentRoute: () => rotaApp,
    path: '/orcamentos',
    beforeLoad: guardaOrcamento,
    component: lazyRouteComponent(
      () => import('@/features/orcamentos/pages/OrcamentosListPage'),
      'OrcamentosListPage',
    ),
  }),
  createRoute({
    getParentRoute: () => rotaApp,
    path: '/orcamentos/novo',
    beforeLoad: guardaOrcamento,
    validateSearch: (busca: Record<string, unknown>): { copiarDe?: string } =>
      typeof busca.copiarDe === 'string' ? { copiarDe: busca.copiarDe } : {},
    component: formularioDeOrcamento,
  }),
  createRoute({
    getParentRoute: () => rotaApp,
    path: '/orcamentos/$id',
    beforeLoad: guardaOrcamento,
    component: formularioDeOrcamento,
  }),
] as const;

/**
 * Operações fiscais.
 *
 * Cadastro de apoio da nota de compra, com a mesma forma dos demais. Fica em
 * Financeiro, e não em Estoque ao lado do tipo de movimentação: o que ela decide
 * é quanto imposto sai do custo, e quem cadastra isso é quem responde pelo
 * fiscal. Controladoria, que digita a nota, só a lê.
 */
const rotasOperacaoFiscal = rotasDeCadastro(
  '/operacoes-fiscais',
  () => import('@/features/operacoes-fiscais/pages/OperacoesFiscaisListPage'),
  'OperacoesFiscaisListPage',
  () => import('@/features/operacoes-fiscais/pages/OperacaoFiscalFormPage'),
  'OperacaoFiscalFormPage',
);

/**
 * Unidades de medida.
 *
 * Cadastro de apoio do insumo, com a mesma forma dos demais. Fica em Compras,
 * ao lado do grupo de insumos: os dois classificam o insumo, um dizendo o que
 * ele é e o outro em que se conta.
 */
const rotasUnidadeMedida = rotasDeCadastro(
  '/unidades-medida',
  () => import('@/features/unidades-medida/pages/UnidadesMedidaListPage'),
  'UnidadesMedidaListPage',
  () => import('@/features/unidades-medida/pages/UnidadeMedidaFormPage'),
  'UnidadeMedidaFormPage',
);

/**
 * Conversões entre unidades de medida (`.spec/015`, decisão 20).
 *
 * Logo depois da unidade, que é a quem elas se referem. O fator daqui é
 * **sugestão**: quem vale é o gravado no item do fornecedor, onde alguém
 * conferiu a entrega.
 */
const rotasConversaoUnidade = rotasDeCadastro(
  '/conversoes-unidade-medida',
  () => import('@/features/conversoes-unidade/pages/ConversoesUnidadeListPage'),
  'ConversoesUnidadeListPage',
  () => import('@/features/conversoes-unidade/pages/ConversaoUnidadeFormPage'),
  'ConversaoUnidadeFormPage',
);

/**
 * Itens do fornecedor — o de‑para do catálogo dele (`.spec/015`, decisão 20).
 *
 * Cadastro de apoio, com a forma dos demais, e ao lado do insumo pelo mesmo
 * motivo: ele liga o código de quem vende ao material daqui.
 *
 * O uso principal é **corrigir**, e não incluir: o vínculo nasce na entrada de
 * nota por XML (`CTL·05`), a cada linha casada. É aqui que um fator gravado
 * errado se conserta — e fator errado multiplica estoque sem estourar nada.
 */
const rotasFornecedorItem = rotasDeCadastro(
  '/fornecedores-itens',
  () => import('@/features/fornecedores-itens/pages/FornecedoresItensListPage'),
  'FornecedoresItensListPage',
  () => import('@/features/fornecedores-itens/pages/FornecedorItemFormPage'),
  'FornecedorItemFormPage',
);

/**
 * Condições de pagamento.
 *
 * As **parcelas não têm rota**: são editadas dentro do formulário da condição e
 * gravadas no mesmo salvamento, como as partes da obra. Uma tela própria
 * permitiria gravar a condição e falhar nas parcelas, deixando no cadastro uma
 * linha que o gerador de títulos não sabe usar.
 */
const rotasCondicaoPagamento = rotasDeCadastro(
  '/condicoes-pagamento',
  () => import('@/features/condicoes-pagamento/pages/CondicoesPagamentoListPage'),
  'CondicoesPagamentoListPage',
  () => import('@/features/condicoes-pagamento/pages/CondicaoPagamentoFormPage'),
  'CondicaoPagamentoFormPage',
);

/**
 * Contratos.
 *
 * Mesma forma dos cadastros — listagem, `/novo` e `/$id` —, ainda que o módulo
 * seja próprio e não fique sob Cadastros: o que a fábrica define é a navegação,
 * não a que módulo a tela pertence.
 */
const rotasContrato = rotasDeCadastro(
  '/contratos',
  () => import('@/features/contratos/pages/ContratosListPage'),
  'ContratosListPage',
  () => import('@/features/contratos/pages/ContratoFormPage'),
  'ContratoFormPage',
);

/**
 * Obras.
 *
 * As **partes da obra não têm rota**: são editadas dentro do formulário dela, e
 * gravadas no mesmo salvamento. Uma tela própria permitiria gravar a obra e
 * falhar nas etapas, deixando o cadastro pela metade.
 */
const rotasObra = rotasDeCadastro(
  '/obras',
  () => import('@/features/obras/pages/ObrasListPage'),
  'ObrasListPage',
  () => import('@/features/obras/pages/ObraFormPage'),
  'ObraFormPage',
);

/**
 * Notas de serviço (`.spec/015`, decisão 90).
 *
 * Documento com situação e três atos — emitir, cancelar, excluir —, e mesmo
 * assim `rotasDeCadastro`: os atos são diálogos sobre o documento aberto, e não
 * rotas. O que a fábrica define é a navegação, e ela é a de sempre: listagem,
 * `/novo` e `/$id`.
 *
 * A guarda sai de `TELAS['FAT·01']`: `faturamento.nota_servico.ver`. As
 * permissões de emitir e de cancelar não entram aqui — quem as confere é a
 * página, botão a botão, e o servidor de novo.
 */
const rotasNotaServico = rotasDeCadastro(
  '/notas-servico',
  () => import('@/features/notas-servico/pages/NotasServicoListPage'),
  'NotasServicoListPage',
  () => import('@/features/notas-servico/pages/NotaServicoFormPage'),
  'NotaServicoFormPage',
);

/**
 * Cadastros de compras.
 *
 * A listagem é uma árvore, não uma tabela, mas as três rotas são as mesmas dos
 * outros cadastros — o que muda é o desenho da tela, não a navegação.
 */
const rotasGrupoInsumo = rotasDeCadastro(
  '/grupos-insumos',
  () => import('@/features/insumos/pages/GruposInsumosListPage'),
  'GruposInsumosListPage',
  () => import('@/features/insumos/pages/GrupoInsumoFormPage'),
  'GrupoInsumoFormPage',
);

/** Insumos. Listagem paginada — a hierarquia mora no cadastro de grupos. */
const rotasInsumo = rotasDeCadastro(
  '/insumos',
  () => import('@/features/insumos/pages/InsumosListPage'),
  'InsumosListPage',
  () => import('@/features/insumos/pages/InsumoFormPage'),
  'InsumoFormPage',
);

/** Depósitos. O local onde o material fica — um por empresa, no mínimo. */
const rotasDeposito = rotasDeCadastro(
  '/depositos',
  () => import('@/features/depositos/pages/DepositosListPage'),
  'DepositosListPage',
  () => import('@/features/depositos/pages/DepositoFormPage'),
  'DepositoFormPage',
);

/**
 * Movimento de caixa.
 *
 * O lançamento tem a mesma forma dos cadastros — listagem, `/novo` e `/$id` —,
 * então usa a mesma fábrica. O extrato é tela única: livro-razão não se abre
 * para edição, e uma rota `/$id` ali só levaria a um formulário que não existe.
 */
const rotasLancamentoContaCorrente = rotasDeCadastro(
  '/lancamentos-conta-corrente',
  () => import('@/features/lancamentos/pages/LancamentosContaCorrenteListPage'),
  'LancamentosContaCorrenteListPage',
  () => import('@/features/lancamentos/pages/LancamentoContaCorrenteFormPage'),
  'LancamentoContaCorrenteFormPage',
);

/**
 * Transferência entre contas correntes.
 *
 * Mesma forma dos cadastros — listagem, `/novo` e `/$id` —, e por isso a mesma
 * fábrica. **Tem `/$id` de edição**, ao contrário da transferência de estoque:
 * lá alterar exigiria desfazer o efeito de um movimento sobre o custo médio, o
 * que é impossível havendo movimento posterior; aqui o efeito é uma soma em cada
 * saldo, e somar o contrário desfaz exatamente.
 */
const rotasTransferenciaContaCorrente = rotasDeCadastro(
  '/transferencias-conta-corrente',
  () =>
    import('@/features/transferencias-conta-corrente/pages/TransferenciasContaCorrenteListPage'),
  'TransferenciasContaCorrenteListPage',
  () => import('@/features/transferencias-conta-corrente/pages/TransferenciaContaCorrenteFormPage'),
  'TransferenciaContaCorrenteFormPage',
);

/**
 * Contas a pagar.
 *
 * Mesma forma dos cadastros — listagem, `/novo` e `/$id` —, e o `/$id` abre em
 * **edição** quando o título foi lançado à mão. O que nasceu da confirmação de
 * uma nota abre em consulta, com a reprogramação de vencimento liberada: o que a
 * fábrica define é a navegação, não o que a tela permite.
 *
 * A baixa **não** tem rota própria. Ela acontece no diálogo do título, e é de
 * propósito: uma tela separada permitiria abrir a baixa sem o título à vista, e é
 * o saldo em aberto dele que decide o valor.
 */
const rotasContaPagar = rotasDeCadastro(
  '/contas-pagar',
  () => import('@/features/contas-pagar/pages/ContasPagarListPage'),
  'ContasPagarListPage',
  () => import('@/features/contas-pagar/pages/ContaPagarFormPage'),
  'ContaPagarFormPage',
);

// `/$id` abre a consulta, e não um formulário de edição: o agrupamento não tem
// `PUT` (decisão 119). A fábrica serve igual — quem decide é a página.
const rotasAgrupamentoContasPagar = rotasDeCadastro(
  '/agrupamentos-titulos',
  () => import('@/features/agrupamentos-contas-pagar/pages/AgrupamentosListPage'),
  'AgrupamentosListPage',
  () => import('@/features/agrupamentos-contas-pagar/pages/AgrupamentoFormPage'),
  'AgrupamentoFormPage',
);

/**
 * Painel de contas a pagar.
 *
 * Rota única, sem `/novo` nem `/$id`: é leitura agregada dos títulos e das
 * baixas, e não há registro de painel a abrir. Mesma forma do painel de compras,
 * do cardex e do extrato de conta corrente, pelo mesmo motivo.
 *
 * O caminho é `/painel-contas-pagar`, e **não** `/contas-pagar/painel`: aninhado
 * sob a listagem, ele herdaria a guarda dela (`pagamento.conta_pagar.ver`), e a
 * permissão do painel é própria de propósito.
 */
const rotaPainelDeContasPagar = createRoute({
  getParentRoute: () => rotaApp,
  path: '/painel-contas-pagar',
  beforeLoad: guardaDe('/painel-contas-pagar'),
  component: lazyRouteComponent(
    () => import('@/features/painel-contas-pagar/pages/PainelDeContasPagarPage'),
    'PainelDeContasPagarPage',
  ),
});

/**
 * Relatório de contas a pagar (`PAG·03`).
 *
 * Rota única, como o relatório de consumo: é leitura agregada, sem registro a
 * abrir. Sob `/relatorios/`, e não sob `/contas-pagar/`, para não herdar a
 * guarda da listagem nem acender "Contas a pagar" no menu (ver o comentário da
 * cota em `cotas.ts`).
 */
const rotaRelatorioContasPagar = createRoute({
  getParentRoute: () => rotaApp,
  path: '/relatorios/contas-pagar',
  beforeLoad: guardaDe('/relatorios/contas-pagar'),
  component: lazyRouteComponent(
    () => import('@/features/relatorio-contas-pagar/pages/RelatorioContasPagarPage'),
    'RelatorioContasPagarPage',
  ),
});

/**
 * Relatório de contas a receber (`REC·03`).
 *
 * Rota única, como o PAG·03, e sob `/relatorios/` pelo mesmo motivo: não herdar
 * a guarda da listagem nem acender "Contas a receber" no menu.
 */
const rotaRelatorioContasReceber = createRoute({
  getParentRoute: () => rotaApp,
  path: '/relatorios/contas-receber',
  beforeLoad: guardaDe('/relatorios/contas-receber'),
  component: lazyRouteComponent(
    () => import('@/features/relatorio-contas-receber/pages/RelatorioContasReceberPage'),
    'RelatorioContasReceberPage',
  ),
});

/**
 * Relatório de pagamentos (`PAG·04`) e de recebimentos (`REC·04`).
 *
 * Rotas únicas, sob `/relatorios/`, pela razão do PAG·03: leitura agregada, sem
 * registro a abrir, e sem acender a listagem de títulos no menu.
 */
const rotaRelatorioPagamentos = createRoute({
  getParentRoute: () => rotaApp,
  path: '/relatorios/pagamentos',
  beforeLoad: guardaDe('/relatorios/pagamentos'),
  component: lazyRouteComponent(
    () => import('@/features/relatorio-pagamentos/pages/RelatorioPagamentosPage'),
    'RelatorioPagamentosPage',
  ),
});

const rotaRelatorioRecebimentos = createRoute({
  getParentRoute: () => rotaApp,
  path: '/relatorios/recebimentos',
  beforeLoad: guardaDe('/relatorios/recebimentos'),
  component: lazyRouteComponent(
    () => import('@/features/relatorio-recebimentos/pages/RelatorioRecebimentosPage'),
    'RelatorioRecebimentosPage',
  ),
});

/**
 * Contas a receber.
 *
 * Mesma forma do contas a pagar, com uma rota a menos de comportamento: aqui o
 * `/$id` abre sempre em **edição**, porque neste módulo não existe título
 * derivado — nenhum documento gera título a receber hoje.
 *
 * A baixa **não** tem rota própria, pelo mesmo motivo do outro lado: uma tela
 * separada permitiria abrir a baixa sem o título à vista, e é o saldo em aberto
 * dele que decide o valor.
 */
const rotasContaReceber = rotasDeCadastro(
  '/contas-receber',
  () => import('@/features/contas-receber/pages/ContasReceberListPage'),
  'ContasReceberListPage',
  () => import('@/features/contas-receber/pages/ContaReceberFormPage'),
  'ContaReceberFormPage',
);

/**
 * Painel de contas a receber.
 *
 * Rota única, sem `/novo` nem `/$id`: é leitura agregada dos títulos e das
 * baixas, e não há registro de painel a abrir. Mesma forma do painel de contas a
 * pagar, do de compras e do cardex.
 *
 * O caminho é `/painel-contas-receber`, e **não** `/contas-receber/painel`:
 * aninhado sob a listagem, ele herdaria a guarda dela
 * (`recebimento.conta_receber.ver`), e a permissão do painel é própria de
 * propósito — é ela que o abre ao Financeiro sem abrir a listagem junto.
 */
const rotaPainelDeContasReceber = createRoute({
  getParentRoute: () => rotaApp,
  path: '/painel-contas-receber',
  beforeLoad: guardaDe('/painel-contas-receber'),
  component: lazyRouteComponent(
    () => import('@/features/painel-contas-receber/pages/PainelDeContasReceberPage'),
    'PainelDeContasReceberPage',
  ),
});

const rotaExtratoContaCorrente = createRoute({
  getParentRoute: () => rotaApp,
  path: '/extrato-conta-corrente',
  beforeLoad: guardaDe('/extrato-conta-corrente'),
  component: lazyRouteComponent(
    () => import('@/features/lancamentos/pages/ExtratoContaCorrentePage'),
    'ExtratoContaCorrentePage',
  ),
});

/**
 * Painel financeiro — o confronto das duas pontas do caixa.
 *
 * Rota única, sem `/novo` nem `/$id`, como os outros painéis: é leitura
 * agregada, e não há registro de painel a abrir.
 *
 * O caminho é `/painel-financeiro`, no mesmo nível dos outros dois painéis de
 * título, e a guarda dele é `financeiro.painel.ver` — não a de nenhum dos dois
 * módulos que ele soma. Aninhado sob qualquer um deles, herdaria a permissão do
 * lado errado e daria o confronto a quem só enxerga metade dele.
 */
const rotaPainelFinanceiro = createRoute({
  getParentRoute: () => rotaApp,
  path: '/painel-financeiro',
  beforeLoad: guardaDe('/painel-financeiro'),
  component: lazyRouteComponent(
    () => import('@/features/painel-financeiro/pages/PainelFinanceiroPage'),
    'PainelFinanceiroPage',
  ),
});

/**
 * Painel de obras — o custo e a receita do canteiro.
 *
 * Rota única, sem `/novo` nem `/$id`, como os outros painéis: é leitura
 * agregada, e não há registro de painel a abrir. A obra individual continua em
 * `/obras/$id`, que é o cadastro dela.
 *
 * A guarda é `contratos.painel_obra.ver`, e **não** `contratos.obra.ver`:
 * aninhá-la sob a permissão do cadastro daria a exposição financeira de cada
 * canteiro a quem a recebeu só para preencher o seletor de obra do rateio.
 */
const rotaPainelDeObras = createRoute({
  getParentRoute: () => rotaApp,
  path: '/painel-obras',
  beforeLoad: guardaDe('/painel-obras'),
  component: lazyRouteComponent(
    () => import('@/features/painel-obras/pages/PainelDeObrasPage'),
    'PainelDeObrasPage',
  ),
});

/**
 * Consumo de insumos na obra (`CTR·05`) — o primeiro relatório.
 *
 * Rota única, como o painel: é leitura agregada, sem registro a abrir. Sob
 * `/relatorios/`, e não sob `/obras/`, para o menu não acender "Obras" com o
 * relatório aberto (ver o comentário da cota em `cotas.ts`).
 *
 * A guarda é `contratos.relatorio_consumo.ver`, e não a do cadastro da obra,
 * pela mesma razão do painel: custo por canteiro não se dá a quem só precisa
 * escolher uma obra num seletor.
 */
const rotaRelatorioConsumoObra = createRoute({
  getParentRoute: () => rotaApp,
  path: '/relatorios/consumo-insumos-obra',
  beforeLoad: guardaDe('/relatorios/consumo-insumos-obra'),
  component: lazyRouteComponent(
    () => import('@/features/relatorio-consumo-obra/pages/RelatorioConsumoObraPage'),
    'RelatorioConsumoObraPage',
  ),
});

const rotaUsuarios = createRoute({
  getParentRoute: () => rotaApp,
  path: '/usuarios',
  beforeLoad: guardaDe('/usuarios'),
  component: lazyRouteComponent(
    () => import('@/features/usuarios/pages/UsuariosListPage'),
    'UsuariosListPage',
  ),
});

const rotaUsuarioNovo = createRoute({
  getParentRoute: () => rotaApp,
  path: '/usuarios/novo',
  beforeLoad: guardaDe('/usuarios'),
  component: lazyRouteComponent(
    () => import('@/features/usuarios/pages/UsuarioFormPage'),
    'UsuarioFormPage',
  ),
});

/**
 * Minha conta: senha e segundo fator do próprio usuário.
 *
 * Guarda de `autenticado` — `guardaDe` a resolve por `podeFazer`, que reconhece
 * o literal. É a tela para onde o login manda quem tem o segundo fator
 * pendente pelo papel.
 */
const rotaMinhaConta = createRoute({
  getParentRoute: () => rotaApp,
  path: '/minha-conta',
  beforeLoad: guardaDe('/minha-conta'),
  component: lazyRouteComponent(
    () => import('@/features/minha-conta/pages/MinhaContaPage'),
    'MinhaContaPage',
  ),
});

/** Recurso singular: uma tela só, sem listagem nem id na rota. */
const rotaConta = createRoute({
  getParentRoute: () => rotaApp,
  path: '/conta',
  beforeLoad: guardaDe('/conta'),
  component: lazyRouteComponent(() => import('@/features/conta/pages/ContaPage'), 'ContaPage'),
});

/**
 * Log de alterações.
 *
 * Rota única, sem `/novo` nem `/$id`: o log não se cria, e o detalhe de um
 * gesto abre em diálogo. É a mesma forma do cardex e do extrato de conta
 * corrente — consulta de livro-razão —, e aqui vale ainda mais: aquele é
 * append-only por natureza, este é append-only por trigger.
 */
const rotaLogAlteracoes = createRoute({
  getParentRoute: () => rotaApp,
  path: '/log-alteracoes',
  beforeLoad: guardaDe('/log-alteracoes'),
  component: lazyRouteComponent(
    () => import('@/features/log-alteracoes/pages/LogAlteracoesListPage'),
    'LogAlteracoesListPage',
  ),
});

const rotaUsuarioEdicao = createRoute({
  getParentRoute: () => rotaApp,
  path: '/usuarios/$id',
  beforeLoad: guardaDe('/usuarios'),
  component: lazyRouteComponent(
    () => import('@/features/usuarios/pages/UsuarioFormPage'),
    'UsuarioFormPage',
  ),
});

/**
 * O destino de quem esbarrou numa guarda de permissão.
 *
 * **Sem guarda própria**: é a única tela do plano do cliente que toda sessão
 * autenticada alcança, e ter de checar permissão para ver "você não tem
 * permissão" seria um laço.
 *
 * O nome da tela vai na busca, e não em estado do roteador, porque a URL é o que
 * sobrevive ao F5 e ao link colado num chamado — que é justamente o que se faz
 * com esta tela.
 */
const rotaSemAcesso = createRoute({
  getParentRoute: () => rotaApp,
  path: '/sem-acesso',
  validateSearch: (busca: Record<string, unknown>): { tela?: string } =>
    typeof busca.tela === 'string' ? { tela: busca.tela } : {},
  component: SemAcesso,
});

/**
 * Console de operação — o outro plano de acesso.
 *
 * Árvore irmã da do cliente, não filha: as guardas são independentes, e ter
 * sessão em um plano não influencia nada no outro. Uma sessão de cliente aberta
 * não abre nenhuma rota daqui, e o contrário também vale.
 */
const rotaLoginOperacao = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/operacao/entrar',
  beforeLoad: () => {
    if (obterSessaoOperador()) throw redirect({ to: '/operacao/contas' });
  },
  component: lazyRouteComponent(
    () => import('@/features/operacao/pages/LoginOperacaoPage'),
    'LoginOperacaoPage',
  ),
});

const rotaOperacao = createRoute({
  getParentRoute: () => rotaRaiz,
  id: 'operacao-autenticado',
  beforeLoad: () => {
    if (!obterSessaoOperador()) throw redirect({ to: '/operacao/entrar' });
  },
  component: OperacaoShell,
});

const rotaOperacaoInicio = createRoute({
  getParentRoute: () => rotaOperacao,
  path: '/operacao',
  beforeLoad: () => {
    throw redirect({ to: '/operacao/contas' });
  },
});

const rotaOperacaoContas = createRoute({
  getParentRoute: () => rotaOperacao,
  path: '/operacao/contas',
  component: lazyRouteComponent(() => import('@/features/operacao/pages/ContasPage'), 'ContasPage'),
});

const rotaOperacaoContaNova = createRoute({
  getParentRoute: () => rotaOperacao,
  path: '/operacao/contas/nova',
  component: lazyRouteComponent(
    () => import('@/features/operacao/pages/ProvisionarContaPage'),
    'ProvisionarContaPage',
  ),
});

/**
 * Catálogos globais, no console.
 *
 * Não é uma conta de cliente, e por isso fica fora de `/operacao/contas`: o NCM
 * é um catálogo só, lido por todas as contas, e a manutenção dele é do sistema.
 */
const rotaOperacaoNcms = createRoute({
  getParentRoute: () => rotaOperacao,
  path: '/operacao/ncms',
  component: lazyRouteComponent(() => import('@/features/operacao/pages/NcmsPage'), 'NcmsPage'),
});

/** Segundo fator do operador — obrigatório; o shell manda para cá até ativar. */
const rotaOperacaoSeguranca = createRoute({
  getParentRoute: () => rotaOperacao,
  path: '/operacao/seguranca',
  component: lazyRouteComponent(
    () => import('@/features/operacao/pages/SegurancaOperacaoPage'),
    'SegurancaOperacaoPage',
  ),
});

/**
 * Acesso negado, e dito com todas as letras.
 *
 * Uma tela, e não um desvio silencioso para o início: quem digitou `/empresas`
 * precisa saber que a tela existe e que falta permissão — cair em outro módulo
 * sem explicação se lê como link quebrado, e a pessoa tenta de novo.
 *
 * Diz **qual** tela, quando sabe: é o que o usuário repete ao administrador para
 * pedir acesso. E fica dentro do `AppShell`, com o menu ao lado: a saída dali é
 * ir para uma tela que ele alcança, que é o que o menu já lista.
 */
function SemAcesso(): React.ReactElement {
  const { tela } = rotaSemAcesso.useSearch();

  return (
    <div className="mx-auto max-w-md py-20 text-center">
      <p className="cota text-grafite-500">Acesso negado</p>
      <p className="mt-2 font-display text-xl font-bold">
        {tela ? `Você não tem acesso a ${tela}` : 'Você não tem acesso a esta tela'}
      </p>
      <p className="mt-2 text-sm text-grafite-600">
        A tela existe, e sua conta não recebeu o papel que a abre. Peça o acesso ao administrador da
        conta — pelo nome que aparece acima, que é como ele a encontra.
      </p>
      <Link to="/" className="mt-6 inline-block">
        <Botao variante="primario">Ir para o início</Botao>
      </Link>
    </div>
  );
}

function SemModulo(): React.ReactElement {
  return (
    <div className="mx-auto max-w-md py-20 text-center">
      <p className="font-display text-xl font-bold">Nenhum módulo liberado</p>
      <p className="mt-2 text-sm text-grafite-600">
        Sua conta está ativa, mas ainda não recebeu papéis com acesso a um módulo. Procure o
        administrador da conta.
      </p>
    </div>
  );
}

function NaoEncontrada(): React.ReactElement {
  return (
    <div className="mx-auto max-w-md py-20 text-center">
      <p className="font-mono text-5xl font-semibold text-papel-400">404</p>
      <p className="mt-3 font-display text-xl font-bold">Página não encontrada</p>
    </div>
  );
}

const arvore = rotaRaiz.addChildren([
  rotaLogin,
  rotaEsqueciSenha,
  rotaDefinirSenha,
  rotaRespostaDaCotacao,
  rotaApp.addChildren([
    rotaInicio,
    rotaEmpresas,
    rotaEmpresaNova,
    rotaEmpresaEdicao,
    rotaFornecedores,
    rotaFornecedorNovo,
    rotaFornecedorEdicao,
    rotaClientes,
    rotaClienteNovo,
    rotaClienteEdicao,
    // Na ordem do processo, como no menu: orçamento, contrato, obra, e a nota
    // de serviço que fatura o que os três combinaram.
    ...rotasOrcamento,
    ...rotasContrato,
    ...rotasObra,
    ...rotasNotaServico,
    // Depois dos documentos: é o cadastro do módulo, e os três vêm primeiro.
    ...rotasItemOrcamento,
    ...rotasContaContabil,
    ...rotasHistoricoBancario,
    ...rotasHistoricoFinanceiro,
    ...rotasGrupoInsumo,
    ...rotasUnidadeMedida,
    // Depois da unidade, que é a quem as conversões se referem.
    ...rotasConversaoUnidade,
    ...rotasInsumo,
    // Depois do insumo, que é uma das duas pontas do de-para.
    ...rotasFornecedorItem,
    ...rotasDeposito,
    ...rotasTipoMovimentacaoEstoque,
    ...rotasLancamentoEstoque,
    ...rotasLancamentoConsumo,
    ...rotasDevolucaoConsumo,
    ...rotasTransferenciaEstoque,
    rotaGeracaoDePedidos,
    rotaDistribuicaoConsumo,
    rotaCardex,
    rotaEstoqueDoDeposito,
    rotaPainelDeCompras,
    ...rotasSolicitacaoCompra,
    ...rotasCotacaoPreco,
    ...rotasPedidoCompra,
    // O caminho curto ao lado do longo, e o atalho de geração logo depois da
    // listagem que ele consome.
    ...rotasListaCompraDireta,
    rotaGeracaoPorLista,
    rotaAprovacoesPendentes,
    rotaEnvioParaAprovacao,
    rotaEnvioDePedidos,
    rotaEntradaPorPedido,
    rotaEntradaPorXml,
    ...rotasNotaCompra,
    ...rotasContaCorrente,
    ...rotasCondicaoPagamento,
    ...rotasOperacaoFiscal,
    ...rotasLancamentoContaCorrente,
    // A transferência ao lado do lançamento manual, e antes do extrato: as duas
    // escrevem no mesmo livro-razão, e o extrato é a leitura do que as duas
    // gravaram.
    ...rotasTransferenciaContaCorrente,
    rotaExtratoContaCorrente,
    ...rotasContaPagar,
    ...rotasAgrupamentoContasPagar,
    rotaPainelDeContasPagar,
    rotaRelatorioContasPagar,
    rotaRelatorioPagamentos,
    ...rotasContaReceber,
    rotaPainelDeContasReceber,
    rotaRelatorioContasReceber,
    rotaRelatorioRecebimentos,
    // Depois dos dois painéis que ele confronta: a ordem desta lista é a de
    // leitura, e o painel financeiro só faz sentido depois de os dois lados
    // existirem.
    rotaPainelFinanceiro,
    // Depois do financeiro, e por isso no fim: é o painel que lê mais coisas —
    // os dois lados pelo rateio da obra, e o consumo, que nem passa pelo contas
    // a pagar.
    rotaPainelDeObras,
    // Logo depois do painel que ele detalha.
    rotaRelatorioConsumoObra,
    rotaUsuarios,
    rotaUsuarioNovo,
    rotaUsuarioEdicao,
    rotaConta,
    // Por último da administração: é a tela que lê o que todas as outras
    // escreveram.
    rotaLogAlteracoes,
    rotaMinhaConta,
    rotaSemAcesso,
  ]),
  rotaLoginOperacao,
  rotaOperacao.addChildren([
    rotaOperacaoInicio,
    rotaOperacaoContas,
    rotaOperacaoContaNova,
    rotaOperacaoNcms,
    rotaOperacaoSeguranca,
  ]),
]);

export const router = createRouter({
  routeTree: arvore,
  defaultNotFoundComponent: NaoEncontrada,
  // Rota carrega em paralelo com o hover do link: o `import()` da página começa
  // antes do clique, e a troca de tela chega a parecer instantânea
  // (regra `bundle-preload`).
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
